import { motion, AnimatePresence } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { getFormEntriesByFormName, getFormEntriesByGroup } from "../services/formServices";
import { FORMNAMES, setBrowserTitle } from "../utilities/codes";
import toast from "react-hot-toast";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { isVideo, mediaSource } from "../utilities/mediautilities";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";
import Breadcrumb from "../components/Breadcrumb.jsx";

const Gallery = () => {
    const { schoolDetails } = useContext(schoolContext);
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [categoryMedia, setCategoryMedia] = useState({});
    const [categories, setCategories] = useState([])
    const { t } = useTranslation()

    useEffect(() => {
        setBrowserTitle(`Gallery`);
        getGalleryCategories();
    }, [])

    const handleCategoryClick = (categoryName) => {
        setBrowserTitle(`${categoryName} - Gallery`);
        setActiveCategory(categoryName);
        if (categoryMedia[categoryName]) { return; }
        getGallery(categoryName);
    };

    const getGalleryCategories = async () => {
        try {
            const response = await getFormEntriesByGroup(FORMNAMES.SCHOOLGALLERY, 'category');
            if (response && response?.length > 0) {
                setCategories(response);

                const actCategory = response[0]?.name
                setActiveCategory(actCategory);

                getGallery(actCategory);
            }

        } catch (error) {
            toast.error(error?.message);
        }
    }

    const prepareMediaList = (categoryWiseMediaList) => {
        let AllMedia = [];
        categoryWiseMediaList?.forEach(eachMedia => {
            AllMedia = AllMedia.concat(eachMedia?.payload?.images?.map(eachI => {
                eachI["category"] = eachMedia?.payload?.category;
                return eachI
            }))
        });
        return AllMedia;
    }


    const getGallery = async (activeCategory) => {
        try {
            if (activeCategory) {
                const filter = { category: { operator: "equals", value: activeCategory } }
                const response = await getFormEntriesByFormName(FORMNAMES.SCHOOLGALLERY, filter);
                setCategoryMedia((prev) => ({
                    ...prev,
                    [activeCategory]: prepareMediaList(response)
                }));
            }
        } catch (error) {
            toast.error(error?.message);
        }
    }

    const nextImage = (e) => {
        e?.stopPropagation()
        setSelectedIndex((prev) =>
            prev === categoryMedia?.[activeCategory]?.length - 1 ? 0 : prev + 1
        );
    };

    const prevImage = (e) => {
        e?.stopPropagation()
        setSelectedIndex((prev) =>
            prev === 0 ? categoryMedia?.[activeCategory]?.length - 1 : prev - 1
        );
    };

    return (
        <>
            {/* Hero Section */}
            <section className="relative bg-cover bg-center py-36 px-6">
                <img
                    src={mediaSource(schoolDetails?.gallery?.fileUrl)}
                    alt={"Page banner"}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            </section>
            <Breadcrumb />


            {/* Main Gallery Section */}
            <section id="gallery" className="py-5 px-6  common-container">
                <motion.h2
                    className="text-4xl font-bold text-primary text-center mb-9"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    {t(I18N_KEYS.GALLERYTITLE)}
                </motion.h2>

                <div
                    className="grid grid-cols-4 gap-2 mb-10 md:flex md:flex-wrap md:justify-center md:gap-4"
                >
                    {categories?.map((category) => (
                        <button
                            key={category?.name}
                            onClick={() => handleCategoryClick(category?.name)}
                            className={`px-1 py-1 md:px-6 md:py-2 text-[12px] md:text-base
                                        rounded-full font-semibold capitalize transition-all
                                        ${activeCategory === category?.name
                                    ? "bg-primary text-accent"
                                    : "bg-accent text-primary border hover:bg-primary hover:text-accent"
                                }`}
                        >
                            {category?.name}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8">
                    {categoryMedia?.[activeCategory]?.map((media, idx) => (
                        <motion.div
                            key={idx}
                            className="relative rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            onClick={() => {
                                setSelectedIndex(idx)
                            }}

                        >
                            <div
                                className="h-64 bg-center bg-cover transform transition-transform duration-500 group-hover:scale-110"
                            >
                                {isVideo(media?.fileName) ? (
                                    <video
                                        controls
                                        controlsList="nodownload"
                                        className="h-full w-full object-cover"
                                    >
                                        <source src={mediaSource(media?.fileUrl)} onError={(e) => mediaSource(e)} />
                                    </video>
                                ) : (
                                    <img
                                        src={mediaSource(media?.fileUrl)}
                                        alt=""
                                        className="h-full w-full"
                                        onError={(e) => mediaSource(e)}
                                    />
                                )}
                            </div>

                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                <p className="text-accent text-lg font-semibold">View{" "}{isVideo(media?.fileName) ? "video" : "image"}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

            </section>

            {selectedIndex !== null && <AnimatePresence>
                <motion.div
                    className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSelectedIndex(null)}
                >
                    {/* Close */}
                    <button
                        onClick={() => setSelectedIndex(null)}
                        className="absolute top-5 right-5 bg-accent rounded-full w-11 h-11 shadow-xl text-xl font-bold hover:bg-primary hover:text-white"
                    >
                        &times;
                    </button>

                    {/* Previous */}
                    {selectedIndex > 0 && <button
                        onClick={prevImage}
                        className="absolute left-6 text-white text-5xl font-bold z-50 hover:text-primary"
                    >
                        &#8249;
                    </button>}

                    {/* Image */}
                    {isVideo(categoryMedia[activeCategory]?.[selectedIndex]?.fileName) ? (
                        <motion.video
                            controls
                            controlsList="nodownload"
                            key={`vid_${selectedIndex}`}
                            alt="Gallery"
                            className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <source src={mediaSource(categoryMedia[activeCategory]?.[selectedIndex]?.fileUrl)} onError={(e) => mediaSource(e)} />
                        </motion.video>
                    ) : (
                        <motion.img
                            key={`img_${selectedIndex}`}
                            src={mediaSource(categoryMedia[activeCategory]?.[selectedIndex]?.fileUrl)}
                            alt="Gallery"
                            className="h-[90vh] w-[95vw] object-contain rounded-lg shadow-2xl"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => mediaSource(e)}
                        />
                    )}

                    {/* Next */}
                    {selectedIndex < categoryMedia[activeCategory]?.length - 1 && <button
                        onClick={nextImage}
                        className="absolute right-6 text-white text-5xl font-bold z-50 hover:text-primary"
                    >
                        &#8250;
                    </button>}

                    {/* Thumbnails Slider */}
                    <div
                        className="absolute bottom-6 flex gap-3 px-4 max-w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {categoryMedia[activeCategory]?.map((media, i) =>
                            isVideo(media?.fileName) ? (
                                <video
                                    onClick={() => setSelectedIndex(i)}
                                    key={i}
                                    className={`h-16 w-24 object-cover rounded-lg cursor-pointer border-2 transition
                                        ${i === selectedIndex
                                            ? "border-primary scale-110"
                                            : "border-transparent opacity-70 hover:opacity-100"
                                        }`}
                                >
                                    <source src={mediaSource(media?.fileUrl)} onError={(e) => mediaSource(e)} />
                                </video>
                            ) : (
                                <img
                                    key={i}
                                    alt=""
                                    src={mediaSource(media?.fileUrl)}
                                    onClick={() => setSelectedIndex(i)}
                                    className={`h-16 w-24 object-cover rounded-lg cursor-pointer border-2 transition
                                        ${i === selectedIndex
                                            ? "border-primary scale-110"
                                            : "border-transparent opacity-70 hover:opacity-100"
                                        }`}
                                    onError={(e) => mediaSource(e)}
                                />
                            ),
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
            }
        </>
    );
}
export default Gallery;